import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmployeeDto } from '@gc/shared';

import { api } from '@/lib/api';
import { ghostPillClass, inputClass, pillClass } from './controls';
import { toFieldErrors, type FieldError } from './useSettings';

/**
 * The "Reset Password" column (SPEC §6.4).
 *
 * A separate action rather than a field on the edit form: it is audited on its
 * own, and it must not be possible to change someone's password as a side
 * effect of correcting their phone number.
 */
export function ResetPasswordDialog({
  employee,
  onClose,
}: {
  employee: EmployeeDto;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;
  const mismatch = confirm.length > 0 && password !== confirm;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (mismatch) return;
    setErrors([]);
    setSubmitting(true);

    try {
      await api.post(`/employees/${employee.id}/reset-password`, { password });
      setDone(true);
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.employees.resetFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-sm rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-1 text-lg font-semibold text-content">
          {t('settings.employees.resetPassword')}
        </h2>
        <p className="mb-4 text-sm text-content-muted">{employee.name}</p>

        {done ? (
          <>
            <p className="text-sm text-success">{t('settings.employees.resetDone')}</p>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} className={pillClass}>
                {t('common.confirm')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-sm text-content-secondary">
                  {t('settings.employees.newPassword')}
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={inputClass}
                />
                {errorFor('password') ? (
                  <p className="mt-1 text-xs text-danger">{errorFor('password')}</p>
                ) : null}
              </label>

              <label className="block">
                <span className="mb-1 block text-sm text-content-secondary">
                  {t('settings.employees.confirmPassword')}
                </span>
                <input
                  type="password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  required
                  autoComplete="new-password"
                  className={inputClass}
                />
                {mismatch ? (
                  <p className="mt-1 text-xs text-danger">
                    {t('settings.employees.passwordMismatch')}
                  </p>
                ) : null}
              </label>
            </div>

            {errorFor('(root)') ? (
              <p role="alert" className="mt-3 text-sm text-danger">
                {errorFor('(root)')}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={onClose} className={ghostPillClass}>
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting || mismatch || password.length < 8}
                className={pillClass}
              >
                {t('common.save')}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
